import React, { FCWithChildren } from 'react';
import Header from '@components/organisms/header/header';
import BottomNavigation from '@components/bottom-navigation/bottom-navigation';
import DynamicFooterWrapper from '@components/organisms/footer/dynamic-footer-wrapper';
import AccessibilityLink from '@components/molecules/accessibility-link/accessibility-link';
import { usePreferences } from '@context/preferences.context';
import { isWebview } from '@helpers/routing';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import SellingOffersBanner from '@components/organisms/selling-offers/selling-offers-banner/selling-offers-banner';
import PageHeader from '@components/organisms/page-header/page-header';

type NegotiationListingsLayoutProps = {
  breadcrumbs: Breadcrumb[];
  title: string;
  shouldShowAccessibilityBanner?: boolean;
  showSellingOffersBanner?: boolean;
};

const NegotiationListingsLayout: FCWithChildren<NegotiationListingsLayoutProps> = (props) => {
  const { children, shouldShowAccessibilityBanner, showSellingOffersBanner, breadcrumbs, title } = props;

  const pageId = title.toLowerCase().replace(' ', '_');

  const { query } = useRouter();
  const { language, siteId, locale } = usePreferences();

  const shouldShowAccessibilityLink = language === 'us' && siteId === 6 && !isWebview(query);
  const { t } = useTranslation('default-layout');

  return (
    <div
      className={clsx(
        locale,
        shouldShowAccessibilityBanner && 'hasBanner',
        shouldShowAccessibilityLink && 'hasAccessibilityLink',
      )}
    >
      {shouldShowAccessibilityLink && <AccessibilityLink />}
      <Head>
        {/* Default Title for all pages */}
        <title>{t('SEO.LOGO_ALT')}</title>
        <meta
          property="og:title"
          content={t('SEO.LOGO_ALT')}
          key="og:title"
        />
      </Head>
      <Header shouldShowBanner={shouldShowAccessibilityBanner} />
      <PageHeader
        title={title}
        breadcrumbs={breadcrumbs}
        banner={showSellingOffersBanner ? <SellingOffersBanner /> : undefined}
        showDivider={true}
        dataCyPrefix={pageId}
      />
      {children}
      <BottomNavigation />
      <DynamicFooterWrapper />
    </div>
  );
};

export default NegotiationListingsLayout;
